/**
 * Product Section Component
 * Displays a horizontal scrollable row of product cards with header and "View All" link
 */

import { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ProductSummary } from '../../services/staticContent';
import { getProductImageUrl } from '../../utils/imageUtils';
import { useCart } from '../../contexts/CartContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useOwnedProducts } from '../../hooks/useOwnedProducts';
import { Product, Currency } from '../../types';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Star,
  ShoppingCart,
  Plus,
  CheckCircle,
  Download,
  LucideIcon,
} from 'lucide-react';

interface ProductSectionProps {
  title: string;
  subtitle?: string;
  icon?: LucideIcon;
  products: ProductSummary[];
  viewAllLink: string;
  viewAllParams?: string;
  loading?: boolean;
  accentColor?: 'primary' | 'amber' | 'green' | 'blue' | 'purple';
}

const accentColors = {
  primary: {
    bg: 'bg-primary-100 dark:bg-primary-900/30',
    text: 'text-primary-600 dark:text-primary-400',
    badge: 'bg-primary-600',
  },
  amber: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    text: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-500',
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    text: 'text-green-600 dark:text-green-400',
    badge: 'bg-green-600',
  },
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    text: 'text-blue-600 dark:text-blue-400',
    badge: 'bg-blue-600',
  },
  purple: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    text: 'text-purple-600 dark:text-purple-400',
    badge: 'bg-purple-600',
  },
};

export function ProductSection({
  title,
  subtitle,
  icon: Icon,
  products,
  viewAllLink,
  viewAllParams,
  loading = false,
  accentColor = 'primary',
}: ProductSectionProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { items: cartItems, addToCart } = useCart();
  const { formatPrice } = useCurrency();
  const { isOwned } = useOwnedProducts();
  const colors = accentColors[accentColor];

  // Check if product is in cart
  const isInCart = useCallback((productId: string) => {
    return cartItems.some(item => item.productId === productId);
  }, [cartItems]);

  // Handle add to cart
  const handleAddToCart = useCallback((product: ProductSummary, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isInCart(product.id)) return;

    // Convert ProductSummary to Product for cart
    const cartProduct: Product = {
      id: product.id,
      title: product.title,
      shortDescription: product.shortDescription,
      longDescription: '',
      category: product.category,
      qualityTier: product.qualityTier,
      price: {
        amount: product.price.amount,
        currency: product.price.currency as Currency,
      },
      imageUrl: product.imageUrl,
      published: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    addToCart(cartProduct);
  }, [addToCart, isInCart]);

  // Check scroll position
  const checkScroll = () => {
    if (!scrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [products]);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const cardWidth = 320; // Approximate card width + gap
    const scrollAmount = direction === 'left' ? -cardWidth * 2 : cardWidth * 2;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    setTimeout(checkScroll, 300);
  };

  const fullViewAllLink = viewAllParams ? `${viewAllLink}?${viewAllParams}` : viewAllLink;

  if (loading) {
    return (
      <section className="py-12">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-8">
            <div className="animate-pulse">
              <div className="h-8 w-48 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
              <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          </div>
          <div className="flex gap-6 overflow-hidden">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex-shrink-0 w-[300px]">
                <div className="card animate-pulse">
                  <div className="h-48 bg-gray-200 dark:bg-gray-700" />
                  <div className="p-5">
                    <div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded mb-3" />
                    <div className="h-6 w-full bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                    <div className="h-4 w-3/4 bg-gray-200 dark:bg-gray-700 rounded mb-4" />
                    <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) return <></>;

  return (
    <section className="py-12">
      <div className="container-custom">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {Icon && (
              <div className={`p-3 rounded-xl ${colors.bg}`}>
                <Icon size={24} className={colors.text} />
              </div>
            )}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
                {title}
              </h2>
              {subtitle && (
                <p className="text-gray-600 dark:text-gray-400 mt-1">{subtitle}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Scroll Buttons (hidden on mobile) */}
            <div className="hidden md:flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                disabled={!canScrollLeft}
                className={`p-2 rounded-full border transition-colors ${
                  canScrollLeft
                    ? 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                }`}
                aria-label="Scroll left"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={!canScrollRight}
                className={`p-2 rounded-full border transition-colors ${
                  canScrollRight
                    ? 'border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                    : 'border-gray-200 dark:border-gray-700 text-gray-300 dark:text-gray-600 cursor-not-allowed'
                }`}
                aria-label="Scroll right"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            {/* View All Link */}
            <Link
              to={fullViewAllLink}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${colors.bg} ${colors.text} hover:opacity-80`}
            >
              View All
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Products Scroll Container */}
        <div className="relative">
          {/* Gradient Fade Left */}
          {canScrollLeft && (
            <div className="hidden md:block absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white dark:from-gray-950 to-transparent z-10 pointer-events-none" />
          )}

          {/* Gradient Fade Right */}
          {canScrollRight && (
            <div className="hidden md:block absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white dark:from-gray-950 to-transparent z-10 pointer-events-none" />
          )}

          {/* Scrollable Products */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory md:snap-none"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                accentColor={accentColor}
                isInCart={isInCart(product.id)}
                isOwned={isOwned(product.id)}
                onAddToCart={handleAddToCart}
                formatPrice={formatPrice}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * Individual Product Card
 */
interface ProductCardProps {
  product: ProductSummary;
  accentColor: keyof typeof accentColors;
  isInCart: boolean;
  isOwned: boolean;
  onAddToCart: (product: ProductSummary, e: React.MouseEvent) => void;
  formatPrice: (amount: number, fromCurrency?: Currency | string) => string;
}

function ProductCard({ product, accentColor, isInCart, isOwned, onAddToCart, formatPrice }: ProductCardProps): JSX.Element {
  const colors = accentColors[accentColor];

  return (
    <div className="flex-shrink-0 w-[280px] md:w-[300px] snap-start group">
      <div className="card overflow-hidden h-full flex flex-col hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
        {/* Image - Clickable link */}
        <Link to={`/products/${product.id}`} className="relative overflow-hidden block">
          <img
            src={getProductImageUrl(product.imageUrl)}
            alt={product.title}
            className="w-full h-44 object-cover bg-gray-200 dark:bg-gray-700 group-hover:scale-105 transition-transform duration-500"
          />

          {/* Owned Badge */}
          {isOwned && (
            <div className="absolute top-3 left-3 px-2.5 py-1 bg-green-600 text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-lg">
              <CheckCircle size={12} />
              Owned
            </div>
          )}

          {/* Category Badge - move to left if not owned */}
          {!isOwned && (
            <div className={`absolute top-3 left-3 px-2.5 py-1 ${colors.badge} text-white text-xs font-medium rounded-full shadow-lg capitalize`}>
              {product.category.replace('-', ' ')}
            </div>
          )}

          {/* Quality Badge */}
          {product.qualityTier === 'premium' && (
            <div className="absolute top-3 right-3 px-2.5 py-1 bg-amber-500 text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-lg">
              <Star size={12} fill="currentColor" />
              Premium
            </div>
          )}

          {/* Hover Overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
              <span className="px-4 py-2 bg-white rounded-full text-sm font-medium text-gray-900 shadow-lg flex items-center gap-2">
                {isOwned ? <Download size={16} /> : <ShoppingCart size={16} />}
                {isOwned ? 'View Downloads' : 'View Details'}
              </span>
            </div>
          </div>
        </Link>

        {/* Content */}
        <div className="p-5 flex flex-col flex-grow">
          {/* Tier Badge */}
          <div className="flex items-center gap-2 mb-3">
            <span className={`text-xs px-2.5 py-1 ${colors.bg} ${colors.text} rounded-full font-medium capitalize`}>
              {product.qualityTier}
            </span>
          </div>

          {/* Title - fixed height for 2 lines */}
          <Link to={`/products/${product.id}`}>
            <h3 className="font-bold text-gray-900 dark:text-gray-100 mb-2 line-clamp-2 min-h-[3rem] group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {product.title}
            </h3>
          </Link>

          {/* Description - fixed height for 2 lines */}
          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2 min-h-[2.5rem] mb-4">
            {product.shortDescription}
          </p>

          {/* Price and Action Button - pushed to bottom */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800 mt-auto">
            <span className="text-xl font-bold text-primary-600 dark:text-primary-400">
              {isOwned ? (
                <span className="text-green-600 dark:text-green-400">Purchased</span>
              ) : (
                formatPrice(product.price.amount, product.price.currency)
              )}
            </span>
            {isOwned ? (
              <Link
                to="/user?tab=products"
                className="p-2.5 text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/30 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                title="Download"
              >
                <Download size={20} />
              </Link>
            ) : isInCart ? (
              <Link
                to="/cart"
                className="p-2.5 text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                title="View Cart"
              >
                <CheckCircle size={20} />
              </Link>
            ) : (
              <button
                onClick={(e) => onAddToCart(product, e)}
                className="p-2.5 text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                title="Add to Cart"
              >
                <Plus size={20} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductSection;
